import {
  DateFormat,
  Display,
  IExportLeaveBalanceData,
  IExportLeaveBalanceMonthlyData,
  IExportLeaveHistoryData,
  ReportPeriod,
  ReportType,
} from '@vacationtracker/shared/types/export'
import { Dayjs } from 'dayjs'

export interface IExportFiltersResponse {
  getLocationList: (IExportFilterProps & { countryCode?: string | null })[]
  getTeamList: IExportTeamProps[]
  getLeaveTypeList: IExportLeaveTypeProps[]
  getLabels: IExportFilterProps[]
  getUsersFiltered: {
    users: IExportFilterProps[]
  }
}

export interface IReportTableProps {
  columnNames: string[]
  data: any[]
  total: number
  page: number
  loading: boolean
  dateFormat: DateFormat
  display: Display
  reportType: ReportType
  editableColumns?: boolean
  hideEmptyColumns?: boolean
  onHideEmptyColumnsChange?: (value: boolean) => void
  loadPrevious: () => void
  loadNext: () => void
  download: () => void
}

export interface IColumnProps {
  title: string
  dataIndex: string
  key: string
  visible: boolean
  render?: (text: string, record: any) => JSX.Element | string
}

export interface IExportFilterProps {
  id: string
  name: string
}

export interface IExportTeamProps extends IExportFilterProps {
  parentId?: string | null
}

export interface IExportLeaveTypeProps extends IExportFilterProps {
  position: number
  color?: string
}

export interface ILeaveReportProps {
  companyData: IExportFiltersResponse
  total: number
  loading: boolean
  page: number
  display: Display
  onFiltersChange: (filters: { [key: string]: string[] }) => void
  onDisplayChange: (display: Display) => void
  loadNext: () => void
  loadPrevious: () => void
  download: () => void
  defaultFilters: Record<string, any>
}

export interface ILeaveHistoryReportProps extends ILeaveReportProps {
  data: IExportLeaveHistoryData[]
  dateFormat: DateFormat
  defaultDates: [Dayjs, Dayjs]
  onDateFormatChange: (dateFormat: DateFormat) => void
  onPeriodChange: (dates: [Dayjs, Dayjs], period?: ReportPeriod) => void
}

export interface ILeaveBalanceReportProps extends ILeaveReportProps {
  data: IExportLeaveBalanceData[]
  defaultDates: [Dayjs, Dayjs]
  calculationStartYear?: string
  onPeriodChange: (dates: [Dayjs, Dayjs], period?: ReportPeriod) => void
}

export interface ILeaveBalanceMonthlyReportProps extends ILeaveReportProps {
  data: IExportLeaveBalanceMonthlyData[]
}

export const reportResultsLimit = 10
export const EXPORT_TIME_WAIT_MINUTES = 3
export const EXPORT_TIME_POLL_INTERVAL_SECONDS = 10
export const EXPORT_LS_KEY = 'waiting_for_export'

export interface IReportQuery {
  lastId?: string
  page?: number
  limit?: number
  // Dynamic filter properties
  [key: string]: string[] | number | string | undefined
  columns?: string[]
}

export interface ILeaveReportQuery extends IReportQuery {
  startDate: string
  endDate: string
}

export interface IBalanceReportQuery extends IReportQuery {
  period: ReportPeriod
  startDate?: string
  endDate?: string
}

const getReportFiltersLSKey = (reportType: ReportType, userId: string) => `${reportType}_${userId}_filters`

export const getFiltersFromLS = (reportType: ReportType, userId: string) => {
  const filters = localStorage.getItem(getReportFiltersLSKey(reportType, userId))
  return filters ? JSON.parse(filters) : {}
}

export const saveFiltersToLS = (filters: any, reportType: ReportType, userId: string) => {
  localStorage.setItem(getReportFiltersLSKey(reportType, userId), JSON.stringify(filters))
}

/**
 * Clears userIds from filters if userStatuses has changed.
 * This prevents invalid user selections when switching between Active/Inactive/Deleted statuses.
 */
export const clearUserIdsOnStatusChange = (filters: { userStatuses?: string[]; userIds?: string[]; [key: string]: any }, previousStatuses: string[] | undefined): void => {
  const prevStatusesKey = (previousStatuses || []).sort().join(',')
  const newStatusesKey = (filters.userStatuses || []).sort().join(',')
  if (prevStatusesKey !== newStatusesKey && filters.userIds) {
    filters.userIds = []
  }
}
